
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Globe, MapPin, Users, Eye, EyeOff, Filter, Database, Map as MapIcon, ChevronDown, CheckSquare, Square, User as UserIcon } from 'lucide-react';
import { Contact, Institution, User, Document } from '../types';

export interface MapModuleProps {
  contacts: Contact[];
  institutions?: Institution[];
  users?: User[];
  documents?: Document[];
}

declare var L: any;

const MapModule: React.FC<MapModuleProps> = ({ contacts, institutions = [], users = [], documents = [] }) => {
  const mapRef = useRef<any>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const markerGroupRef = useRef<any>(null);
  
  const [isMapReady, setIsMapReady] = useState(false);
  const [activeCategories, setActiveCategories] = useState<Set<string>>(new Set());
  const [activeCreators, setActiveCreators] = useState<Set<string>>(new Set());
  const [activeInstitutionTypes, setActiveInstitutionTypes] = useState<Set<string>>(new Set());
  const [showDocuments, setShowDocuments] = useState(true);

  const geoDocuments = useMemo(() => documents.filter(d => d.latitude && d.longitude), [documents]);

  const SP_STATE_CENTER: [number, number] = [-23.5505, -46.6333];

  const SYSTEM_COLORS: Record<string, string> = {
    'Cidadão': '#0ea0ff',
    'Autoridade': '#6366f1',
    'Entidade': '#10b981',
    'Imprensa': '#f59e0b',
    'Órgão': '#8b5cf6',
    'Fornecedor': '#64748b',
    'Liderança Regional': '#f43f5e',
  };

  const getDynamicColor = (name: string) => {
    if (SYSTEM_COLORS[name]) return SYSTEM_COLORS[name];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    const h = Math.abs(hash % 360);
    return `hsl(${h}, 70%, 50%)`;
  };

  const uniqueContactCategories = useMemo(() => Array.from(new Set(contacts.map(c => c.type))).sort(), [contacts]);
  
  // Mapeamento de IDs de criadores para nomes
  const uniqueCreators = useMemo(() => {
    const creatorIds = Array.from(new Set(contacts.map(c => c.created_by || 'unknown'))).sort();
    return creatorIds.map(id => {
      if (id === 'unknown') return { id, name: 'Desconhecido / Sistema' };
      const user = users.find(u => u.id === id);
      // Fix: Added string cast to 'id' to resolve 'Property substring does not exist on type unknown' error
      return { id, name: user?.name || `Usuário (${(id as string).substring(0, 4)})` };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [contacts, users]);
  
  const uniqueInstitutionTypes = useMemo(() => Array.from(new Set(institutions.map(i => i.type))).sort(), [institutions]);

  useEffect(() => {
    if (uniqueContactCategories.length > 0 && activeCategories.size === 0) {
      setActiveCategories(new Set(uniqueContactCategories));
    }
    if (uniqueCreators.length > 0 && activeCreators.size === 0) {
      setActiveCreators(new Set(uniqueCreators.map(c => c.id)));
    }
    if (uniqueInstitutionTypes.length > 0 && activeInstitutionTypes.size === 0) {
      setActiveInstitutionTypes(new Set(uniqueInstitutionTypes));
    }
  }, [uniqueContactCategories, uniqueCreators, uniqueInstitutionTypes]);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    mapRef.current = L.map(mapContainerRef.current, { zoomControl: false }).setView(SP_STATE_CENTER, 8);
    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; CARTO',
      maxZoom: 20
    }).addTo(mapRef.current);
    markerGroupRef.current = L.featureGroup().addTo(mapRef.current);
    L.control.zoom({ position: 'bottomright' }).addTo(mapRef.current);
    setIsMapReady(true);
    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, []);

  useEffect(() => {
    if (!isMapReady || !mapRef.current || !markerGroupRef.current) return;
    markerGroupRef.current.clearLayers();

    contacts.forEach(contact => {
      const creatorKey = contact.created_by || 'unknown';
      const matchesCategory = activeCategories.has(contact.type);
      const matchesCreator = activeCreators.size === 0 || activeCreators.has(creatorKey);

      if (contact.latitude && contact.longitude && matchesCategory && matchesCreator) {
        const color = getDynamicColor(contact.type);
        const creatorName = uniqueCreators.find(c => c.id === creatorKey)?.name || 'Desconhecido';
        
        const icon = L.divIcon({
          className: 'custom-div-icon',
          html: `<div class="w-7 h-7 rounded-full border-2 border-white shadow-lg flex items-center justify-center text-white font-black text-[10px]" style="background-color: ${color}">${contact.name.charAt(0)}</div>`,
          iconSize: [28, 28], iconAnchor: [14, 28]
        });
        L.marker([contact.latitude, contact.longitude], { icon })
          .bindPopup(`<div class="p-2 font-sans"><p class="text-[9px] font-black uppercase text-slate-400 mb-1">${contact.type}</p><h4 class="font-bold text-sm text-navy-950">${contact.name}</h4><p class="text-[10px] text-slate-500">${contact.neighborhood || ''}, ${contact.city}</p><p class="text-[9px] font-bold text-parliament-600 mt-1 uppercase">Cadastrado por: ${creatorName}</p></div>`)
          .addTo(markerGroupRef.current);
      }
    });

    institutions.forEach(inst => {
      if (inst.latitude && inst.longitude && activeInstitutionTypes.has(inst.type)) {
        const color = getDynamicColor(inst.type);
        const icon = L.divIcon({
          className: 'custom-div-icon',
          html: `<div class="w-8 h-8 rounded-xl border-2 border-white shadow-lg flex items-center justify-center text-white" style="background-color: ${color}"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg></div>`,
          iconSize: [32, 32], iconAnchor: [16, 32]
        });
        L.marker([inst.latitude, inst.longitude], { icon })
          .bindPopup(`<div class="p-2 font-sans"><p class="text-[9px] font-black uppercase text-slate-400 mb-1">${inst.type}</p><h4 class="font-bold text-sm text-navy-950">${inst.name}</h4><p class="text-[10px] text-slate-500">${inst.address || ''}</p></div>`)
          .addTo(markerGroupRef.current);
      }
    });

    if (showDocuments) {
      geoDocuments.forEach(doc => {
        const icon = L.divIcon({
          className: 'custom-div-icon',
          html: `<div class="w-8 h-8 rounded-xl border-2 border-white shadow-lg flex items-center justify-center text-white" style="background-color: #dc2626"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg></div>`,
          iconSize: [32, 32], iconAnchor: [16, 32]
        });
        L.marker([doc.latitude, doc.longitude], { icon })
          .bindPopup(`<div class="p-2 font-sans"><p class="text-[9px] font-black uppercase text-red-500 mb-1">Ofício ${doc.document_number || ''}</p><h4 class="font-bold text-sm text-navy-950">${doc.subject}</h4><p class="text-[10px] text-slate-500">${doc.address_street || ''}${doc.address_number ? ', ' + doc.address_number : ''} — ${doc.address_neighborhood || ''}</p><p class="text-[9px] font-bold text-slate-400 uppercase mt-1">${doc.status}</p></div>`)
          .addTo(markerGroupRef.current);
      });
    }

    if (markerGroupRef.current.getLayers().length > 0) {
      mapRef.current.fitBounds(markerGroupRef.current.getBounds().pad(0.1));
    }
  }, [isMapReady, contacts, institutions, activeCategories, activeCreators, activeInstitutionTypes, uniqueCreators, geoDocuments, showDocuments]);

  const toggleFilter = (id: string, type: 'category' | 'creator' | 'institution') => {
    if (type === 'institution') {
      const next = new Set(activeInstitutionTypes);
      if (next.has(id)) next.delete(id); else next.add(id);
      setActiveInstitutionTypes(next);
    } else if (type === 'creator') {
      const next = new Set(activeCreators);
      if (next.has(id)) next.delete(id); else next.add(id);
      setActiveCreators(next);
    } else {
      const next = new Set(activeCategories);
      if (next.has(id)) next.delete(id); else next.add(id);
      setActiveCategories(next);
    }
  };

  return (
    <div className="h-full flex flex-col gap-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 px-2">
        <div>
          <h2 className="text-4xl font-black text-navy-950 dark:text-white tracking-tighter">Inteligência Geográfica</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-1 font-medium text-sm">Visualização de base territorial e distribuição por equipe.</p>
        </div>
      </div>

      <div className="flex-1 bg-white dark:bg-navy-900 rounded-[40px] p-2 border border-neutral-200 dark:border-navy-800 shadow-2xl relative min-h-[500px] overflow-hidden">
        <div ref={mapContainerRef} className="w-full h-full rounded-[36px] z-10" />
        
        <div className="absolute top-8 right-8 z-20 w-72 bg-white/90 dark:bg-navy-950/90 backdrop-blur-xl p-6 rounded-[32px] shadow-2xl border border-white/20 dark:border-navy-800 max-h-[85%] overflow-y-auto custom-scrollbar">
           <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-4"><Filter size={12}/> Filtros de Mapa</h4>
           
           <div className="space-y-6">
              <section>
                <p className="text-[9px] font-black text-parliament-600 uppercase tracking-widest mb-3 flex items-center gap-2"><Users size={12}/> Categorias</p>
                <div className="space-y-1">
                  {uniqueContactCategories.map(cat => (
                    <button key={cat} onClick={() => toggleFilter(cat, 'category')} className={`w-full flex items-center justify-between p-2 rounded-xl border transition-all ${activeCategories.has(cat) ? 'bg-white dark:bg-navy-800 border-neutral-100 dark:border-navy-700 shadow-sm' : 'opacity-40 border-transparent'}`}>
                       <div className="flex items-center gap-2 min-w-0">
                          <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: getDynamicColor(cat) }} />
                          <span className="text-[10px] font-bold text-navy-900 dark:text-white truncate">{cat}</span>
                       </div>
                       {activeCategories.has(cat) ? <Eye size={12} className="text-parliament-600" /> : <EyeOff size={12} />}
                    </button>
                  ))}
                </div>
              </section>

              {uniqueCreators.length > 0 && (
                <section className="pt-4 border-t border-neutral-100 dark:border-navy-800">
                  <p className="text-[9px] font-black text-amber-600 uppercase tracking-widest mb-3 flex items-center gap-2"><UserIcon size={12}/> Por Cadastrador</p>
                  <div className="space-y-1">
                    {uniqueCreators.map(creator => (
                      <button key={creator.id} onClick={() => toggleFilter(creator.id, 'creator')} className={`w-full flex items-center justify-between p-2 rounded-xl transition-all ${activeCreators.has(creator.id) ? 'text-navy-950 dark:text-white' : 'opacity-40 text-slate-400'}`}>
                         <div className="flex items-center gap-2 min-w-0">
                            {activeCreators.has(creator.id) ? <CheckSquare size={14} className="text-amber-600" /> : <Square size={14} />}
                            <span className="text-[10px] font-bold truncate">{creator.name}</span>
                         </div>
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {geoDocuments.length > 0 && (
                <section className="pt-4 border-t border-neutral-100 dark:border-navy-800">
                  <p className="text-[9px] font-black text-red-600 uppercase tracking-widest mb-3 flex items-center gap-2"><MapPin size={12}/> Ofícios ({geoDocuments.length})</p>
                  <button onClick={() => setShowDocuments(!showDocuments)} className={`w-full flex items-center justify-between p-2 rounded-xl border transition-all ${showDocuments ? 'bg-white dark:bg-navy-800 border-neutral-100 dark:border-navy-700 shadow-sm' : 'opacity-40 border-transparent'}`}>
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500 shrink-0" />
                      <span className="text-[10px] font-bold text-navy-900 dark:text-white">Documentos</span>
                    </div>
                    {showDocuments ? <Eye size={12} className="text-red-600" /> : <EyeOff size={12} />}
                  </button>
                </section>
              )}

              {uniqueInstitutionTypes.length > 0 && (
                <section className="pt-4 border-t border-neutral-100 dark:border-navy-800">
                  <p className="text-[9px] font-black text-emerald-600 uppercase tracking-widest mb-3 flex items-center gap-2"><Database size={12}/> Pontos Estratégicos</p>
                  <div className="space-y-1">
                    {uniqueInstitutionTypes.map(type => (
                      <button key={type} onClick={() => toggleFilter(type, 'institution')} className={`w-full flex items-center justify-between p-2 rounded-xl border transition-all ${activeInstitutionTypes.has(type) ? 'bg-white dark:bg-navy-800 border-neutral-100 dark:border-navy-700 shadow-sm' : 'opacity-40 border-transparent'}`}>
                         <div className="flex items-center gap-2 min-w-0">
                            <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: getDynamicColor(type) }} />
                            <span className="text-[10px] font-bold text-navy-900 dark:text-white truncate">{type}</span>
                         </div>
                         {activeInstitutionTypes.has(type) ? <Eye size={12} className="text-emerald-600" /> : <EyeOff size={12} />}
                      </button>
                    ))}
                  </div>
                </section>
              )}
           </div>
        </div>
      </div>
    </div>
  );
};

export default MapModule;
